<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="admin-modal" @click.stop>
      <div class="admin-header">
        <h2>⚙️ Administration</h2>
        <button class="close-btn" @click="$emit('close')">×</button>
      </div>

      <div class="admin-body">
        <div v-if="loading" class="muted">Laddar…</div>
        <div v-else>
          <section class="settings">
            <label class="toggle">
              <input type="checkbox" v-model="settings.registrationOpen" @change="saveSettings" />
              <span>Registrering öppen</span>
            </label>
            <p class="hint">Låt nya användare skapa konton.</p>

            <label class="toggle">
              <input type="checkbox" v-model="settings.requireApproval" @change="saveSettings" />
              <span>Kräv godkännande av nya konton</span>
            </label>
            <p class="hint">Nya konton måste godkännas av en admin innan de kan logga in.</p>
          </section>

          <section class="pending">
            <h3>Väntar på godkännande <span class="count">{{ pending.length }}</span></h3>
            <div v-if="pending.length === 0" class="muted">Inga konton väntar.</div>
            <ul v-else class="pending-list">
              <li v-for="u in pending" :key="u.id">
                <div class="pinfo">
                  <strong>{{ u.name }}</strong>
                  <span class="pemail">{{ u.email }}</span>
                  <span v-if="!u.verified" class="unverified">ej verifierad</span>
                </div>
                <div class="pactions">
                  <button class="approve" @click="approve(u.id)" :disabled="busy">Godkänn</button>
                  <button class="reject" @click="reject(u.id)" :disabled="busy">Avvisa</button>
                </div>
              </li>
            </ul>
          </section>

          <p v-if="error" class="error">{{ error }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api.js';
import authService from '@/services/auth.js';

export default {
  name: 'AdminPanel',
  emits: ['close'],
  data() {
    return {
      loading: true,
      busy: false,
      error: null,
      settings: { registrationOpen: true, requireApproval: false },
      pending: []
    };
  },
  async mounted() {
    await this.reload();
  },
  methods: {
    token() { return authService.getToken(); },
    async reload() {
      this.loading = true;
      this.error = null;
      try {
        this.settings = await apiService.getAdminSettings(this.token());
        const p = await apiService.getPendingUsers(this.token());
        this.pending = p.pending || [];
      } catch (e) {
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },
    async saveSettings() {
      this.error = null;
      try {
        this.settings = await apiService.updateAdminSettings(
          { registrationOpen: this.settings.registrationOpen, requireApproval: this.settings.requireApproval },
          this.token()
        );
      } catch (e) {
        this.error = e.message;
      }
    },
    async approve(id) {
      this.busy = true;
      try {
        await apiService.approvePendingUser(id, this.token());
        this.pending = this.pending.filter(u => u.id !== id);
      } catch (e) { this.error = e.message; } finally { this.busy = false; }
    },
    async reject(id) {
      this.busy = true;
      try {
        await apiService.rejectPendingUser(id, this.token());
        this.pending = this.pending.filter(u => u.id !== id);
      } catch (e) { this.error = e.message; } finally { this.busy = false; }
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.admin-modal {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 48px rgba(16, 24, 40, 0.24);
}

.admin-header {
  background: linear-gradient(135deg, #2e7d32, #1b5e20);
  color: #fff;
  padding: 1.25rem 1.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.admin-header h2 { margin: 0; font-weight: 800; }

.close-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.5rem;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 50%;
}
.close-btn:hover { background: rgba(255, 255, 255, 0.18); }

.admin-body { padding: 1.75rem; overflow-y: auto; }

.settings { margin-bottom: 1.5rem; }

.toggle {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-weight: 700;
  color: #1f2933;
  cursor: pointer;
  margin-top: 1rem;
}
.toggle input { width: 18px; height: 18px; accent-color: #2e7d32; }

.hint { margin: 0.25rem 0 0 1.75rem; color: #6b7684; font-size: 0.88rem; }

.pending h3 {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #1f2933;
  border-top: 1px solid #e6e9ee;
  padding-top: 1.25rem;
}
.count {
  background: #e8f3e9;
  color: #1b5e20;
  border-radius: 999px;
  padding: 0.1rem 0.6rem;
  font-size: 0.85rem;
}

.muted { color: #6b7684; }

.pending-list { list-style: none; padding: 0; margin: 0.5rem 0 0; }
.pending-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f2f5;
}
.pinfo { display: flex; flex-direction: column; }
.pemail { color: #6b7684; font-size: 0.88rem; }
.unverified {
  color: #b54708;
  font-size: 0.75rem;
  font-weight: 700;
}
.pactions { display: flex; gap: 0.5rem; }
.approve, .reject {
  border: none;
  border-radius: 999px;
  padding: 0.5rem 1rem;
  font-weight: 700;
  cursor: pointer;
}
.approve { background: #2e7d32; color: #fff; }
.reject { background: #fbeaea; color: #b42318; }
.error { color: #b42318; margin-top: 1rem; }
</style>
